// Error boundary for the Talent dashboard route. Modern.js renders this
// component when the page throws during render, injecting `error` + `reset`.
// Mirrors `LearningDashboardErrorBoundary`'s pattern (co-located with the view).
//
// REPAIR (ANLT-598): also reused directly (not via a thrown render error) by
// `DashboardsV2TalentView` for the modules-access QUERY error branch — same
// copy/shape, `reset` wired to the hook's `refetch` instead of Modern.js's
// route-boundary reset. Shape is generic enough (`error: Error`, `reset: ()
// => void`) that this dual use doesn't need a second component.
import { useAddI18nLocales, useTranslation } from './i18n';

type TErrorBoundaryProps = {
  error: Error;
  reset: () => void;
};

export const TalentDashboardErrorBoundary = ({
  error,
  reset,
}: TErrorBoundaryProps) => {
  useAddI18nLocales();
  const { t } = useTranslation();

  // REPAIR (ANLT-598/security): `error.message` used to render verbatim here —
  // for the modules-access branch that leaks the raw ORG_UNAVAILABLE driver
  // message, which BE rule 7 keeps deliberately opaque (a caller could
  // enumerate organizations from it) and the security review flags as raw
  // GraphQL error exposure. The copy below stays generic, matching
  // `TalentErrorPanel`'s CONTENT_ERROR_* tone; `error` is kept for a
  // dev-facing diagnostic only, mirroring the console.error pattern in
  // `useViewerAllowedHeadquarters` — never rendered to the user.
  console.error('[TalentDashboardErrorBoundary]', error);

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-cds-py-8 tw-cds-gap-5">
      <p className="tw-cds-font-body-02 tw-cds-text-secondary">
        {t('ERROR_TITLE')}
      </p>
      <p className="tw-cds-font-body-01 tw-cds-text-subtle">
        {t('CONTENT_ERROR_DESCRIPTION')}
      </p>
      <button
        type="button"
        className="tw-cds-font-label-01 tw-cds-text-brand"
        onClick={reset}
      >
        {t('ERROR_RETRY')}
      </button>
    </div>
  );
};
